import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { notify } from '../components/common/AppToast';
import useAppConfirm from './useAppConfirm';
import klubMagazineService from '../services/klubMagazineService';
import agendaSetService from '../services/agendaSetService';
import agendaExportService from '../services/agendaExportService';
import {
  DEFAULT_AGENDA_DATES,
  DEFAULT_COMMERCANTS,
  DEFAULT_DATES,
  DEFAULT_OFFRES,
  EMPTY_COMMERCANT_EDIT,
  EMPTY_OFFRE_EDIT,
  EMPTY_SET_EDIT,
  FUTURE_MONTHS_AHEAD,
  addMonthsYm,
  canonicalMonth,
  compareProposeOrder,
  formatMonthLabel,
  isNouveauBeforeAncien,
  primaryStatut,
  workingMonth,
} from '../components/klub/magazine/magazineHelpers';

function datesApiForTab(tab) {
  return tab === 'agenda' ? agendaExportService : agendaSetService;
}

function useKlubMagazine() {
  const { ask, modal } = useAppConfirm();
  const [month, setMonth] = useState(() => canonicalMonth(workingMonth()));
  const [parutions, setParutions] = useState([]);
  const [parution, setParution] = useState(null);
  const [agendaParution, setAgendaParution] = useState(null);
  const [agendaExportParution, setAgendaExportParution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('commercants');
  const [error, setError] = useState('');
  const [targetCommercants, setTargetCommercants] = useState(DEFAULT_COMMERCANTS);
  const [targetOffres, setTargetOffres] = useState(DEFAULT_OFFRES);
  const [targetDates, setTargetDates] = useState(DEFAULT_DATES);
  const [targetAgendaDates, setTargetAgendaDates] = useState(DEFAULT_AGENDA_DATES);

  const [editCommercant, setEditCommercant] = useState(null);
  const [editOffre, setEditOffre] = useState(null);
  const [editDate, setEditDate] = useState(null);
  const [commercantForm, setCommercantForm] = useState(EMPTY_COMMERCANT_EDIT);
  const [offreForm, setOffreForm] = useState(EMPTY_OFFRE_EDIT);
  const [dateForm, setDateForm] = useState(EMPTY_SET_EDIT);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKind, setPickerKind] = useState('commercants'); // commercants | offres | set | agenda
  const [candidates, setCandidates] = useState(null);
  const [offreCandidates, setOffreCandidates] = useState(null);
  const [dateCandidates, setDateCandidates] = useState([]);
  const [thematiques, setThematiques] = useState([]);
  const [pickerThematiqueId, setPickerThematiqueId] = useState('');
  const [pickerDateFrom, setPickerDateFrom] = useState('');
  const [pickerDateTo, setPickerDateTo] = useState('');
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateBucket, setCandidateBucket] = useState('all');
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);

  // Historique + futurs : tout est modifiable (les stats suivent via les M2M)
  const editable = true;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const loadList = useCallback(async () => {
    try {
      const data = await klubMagazineService.listParutions();
      setParutions(data.parutions ?? []);
    } catch (err) {
      notify.error(err.message);
    }
  }, []);

  const loadParution = useCallback(async (ym) => {
    setLoading(true);
    setError('');
    const canon = canonicalMonth(ym);
    try {
      const [data, setData, exportData] = await Promise.all([
        klubMagazineService.getParution(canon),
        agendaSetService.getParution(canon).catch(() => ({ dates: [] })),
        agendaExportService.getParution(canon).catch(() => ({ dates: [] })),
      ]);
      setParution(data);
      setAgendaParution(setData);
      setAgendaExportParution(exportData);
    } catch (err) {
      setParution(null);
      setAgendaParution(null);
      setAgendaExportParution(null);
      setError(err.message);
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadParution(month);
  }, [month, loadParution]);

  useEffect(() => {
    let cancelled = false;
    const ym = canonicalMonth(month);
    (async () => {
      try {
        const [cData, oData] = await Promise.all([
          klubMagazineService.candidates(ym).catch(() => null),
          klubMagazineService.offreCandidates(ym).catch(() => null),
        ]);
        if (!cancelled) {
          if (cData) setCandidates(cData);
          if (oData) setOffreCandidates(oData);
        }
      } catch {
        // Pastilles optionnelles
      }
    })();
    return () => { cancelled = true; };
  }, [month]);

  const commercants = parution?.commercants ?? [];
  const offres = parution?.offres ?? [];
  const setDates = agendaParution?.dates ?? [];
  const agendaDates = agendaExportParution?.dates ?? [];
  const dates = tab === 'agenda' ? agendaDates : setDates;
  const isDatesTab = tab === 'set' || tab === 'agenda';
  const datesService = datesApiForTab(tab);

  const statutByCommercantId = useMemo(() => {
    if (!candidates) return new Map();
    const map = new Map();
    ['non_parue', 'parue', 'a_renouveller'].forEach((bucket) => {
      (candidates[bucket] ?? []).forEach((c) => {
        map.set(c.id, c.statut || bucket);
      });
    });
    return map;
  }, [candidates]);

  const statutByOffreId = useMemo(() => {
    if (!offreCandidates) return new Map();
    const map = new Map();
    ['non_parue', 'parue', 'a_renouveller'].forEach((bucket) => {
      (offreCandidates[bucket] ?? []).forEach((o) => {
        map.set(o.id, o.statut || bucket);
      });
    });
    return map;
  }, [offreCandidates]);

  const resolveCommercantStatut = useCallback((row) => (
    primaryStatut(row) || statutByCommercantId.get(row.commercant_id) || null
  ), [statutByCommercantId]);

  const resolveOffreStatut = useCallback((row) => (
    primaryStatut(row) || statutByOffreId.get(row.offre_id) || null
  ), [statutByOffreId]);

  const monthOptions = useMemo(() => {
    const months = new Set();
    const work = canonicalMonth(workingMonth());
    months.add(work);
    for (let i = 1; i <= FUTURE_MONTHS_AHEAD; i += 1) {
      months.add(canonicalMonth(addMonthsYm(work, i)));
    }
    parutions.forEach((p) => {
      if (p.month) months.add(canonicalMonth(p.month));
    });
    if (month) months.add(canonicalMonth(month));
    return Array.from(months)
      .filter((ym) => !ym.endsWith('-08'))
      .sort((a, b) => b.localeCompare(a));
  }, [parutions, month]);

  useEffect(() => {
    const canon = canonicalMonth(month);
    if (canon !== month) {
      setMonth(canon);
      return;
    }
    if (monthOptions.length && !monthOptions.includes(month)) {
      setMonth(canonicalMonth(workingMonth()));
    }
  }, [monthOptions, month]);

  const toCommercantPayload = useCallback((rows) => rows.map((c, index) => ({
    commercant_id: c.commercant_id,
    position: index,
    is_nouveau: Boolean(c.is_nouveau),
    nom: c.nom,
    activite: c.activite,
    complement: c.complement,
    copyright: c.copyright,
    photo_path: c.photo_path,
  })), []);

  const toOffrePayload = useCallback((rows) => rows.map((o, index) => ({
    offre_id: o.offre_id,
    position: index,
    is_nouveau: Boolean(o.is_nouveau),
    commercant_label: o.commercant_label,
    description: o.description,
    remise: o.remise,
    prix: o.prix,
    prix_barre: o.prix_barre,
    apartirde: o.apartirde,
    copyright: o.copyright,
    photo_path: o.photo_path,
  })), []);

  const toDatePayload = useCallback((rows) => rows.map((d, index) => ({
    agenda_id: d.agenda_id,
    position: index,
    thematique: d.thematique,
    titre: d.titre,
    date_label: d.date_label,
    resume: d.resume,
    prix: d.prix,
    infos: d.infos,
    lieu: d.lieu,
    image_path: d.image_path,
  })), []);

  const persistCommercants = useCallback(async (rows) => {
    if (!isNouveauBeforeAncien(rows)) {
      notify.warning('Impossible de placer un commerçant ancien avant un nouveau.');
      return false;
    }
    setBusy(true);
    try {
      const data = await klubMagazineService.replaceCommercants(month, toCommercantPayload(rows));
      setParution(data);
      return true;
    } catch (err) {
      notify.error(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  }, [month, toCommercantPayload]);

  const persistOffres = useCallback(async (rows) => {
    if (!isNouveauBeforeAncien(rows)) {
      notify.warning('Impossible de placer une offre ancienne avant une nouvelle.');
      return false;
    }
    setBusy(true);
    try {
      const data = await klubMagazineService.replaceOffres(month, toOffrePayload(rows));
      setParution(data);
      return true;
    } catch (err) {
      notify.error(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  }, [month, toOffrePayload]);

  const persistDates = useCallback(async (rows, datesTab = tab) => {
    const api = datesApiForTab(datesTab);
    setBusy(true);
    try {
      const data = await api.replaceDates(month, toDatePayload(rows));
      if (datesTab === 'agenda') {
        setAgendaExportParution(data);
      } else {
        setAgendaParution(data);
      }
      return true;
    } catch (err) {
      notify.error(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  }, [month, toDatePayload, tab]);

  const handlePropose = useCallback(async () => {
    if (isDatesTab) {
      const defaultCount = tab === 'agenda' ? DEFAULT_AGENDA_DATES : DEFAULT_DATES;
      const maxCount = tab === 'agenda' ? 200 : 100;
      const target = tab === 'agenda' ? targetAgendaDates : targetDates;
      const count = Math.max(1, Math.min(maxCount, Number(target) || defaultCount));
      const hasItems = dates.length > 0;
      const ok = await ask({
        title: 'Proposer des dates ?',
        message: hasItems
          ? `La sélection actuelle sera remplacée par ${count} date(s) à thématiques variées.`
          : `Sélection automatique de ${count} date(s) à thématiques variées.`,
        confirmLabel: 'Proposer',
        confirmVariant: 'primary',
      });
      if (!ok) return;
      setBusy(true);
      try {
        const data = await datesService.propose(month, true, count);
        if (tab === 'agenda') {
          setAgendaExportParution(data);
        } else {
          setAgendaParution(data);
        }
        const n = Array.isArray(data?.dates) ? data.dates.length : 0;
        if (n === 0) {
          notify.warning('Aucune date publiée entre ce mois et les 3 suivants.');
        } else {
          notify.success(`${n} date(s) proposée(s)`);
        }
      } catch (err) {
        notify.error(err.message);
      } finally {
        setBusy(false);
      }
      return;
    }

    const isCommercants = tab === 'commercants';
    const count = isCommercants
      ? Math.max(1, Math.min(100, Number(targetCommercants) || DEFAULT_COMMERCANTS))
      : Math.max(1, Math.min(100, Number(targetOffres) || DEFAULT_OFFRES));
    const label = isCommercants ? 'commerçants' : 'offres';
    const hasItems = isCommercants ? commercants.length > 0 : offres.length > 0;
    const ok = await ask({
      title: `Proposer des ${label} ?`,
      message: hasItems
        ? `La sélection ${label} actuelle sera remplacée par ${count} proposition(s).`
        : `Sélection automatique de ${count} ${label}.`,
      confirmLabel: 'Proposer',
      confirmVariant: 'primary',
    });
    if (!ok) return;

    setBusy(true);
    try {
      const scope = isCommercants ? 'commercants' : 'offres';
      const keepOther = isCommercants
        ? toOffrePayload(offres)
        : toCommercantPayload(commercants);
      await klubMagazineService.propose(month, true, {
        scope,
        commercants_count: isCommercants ? count : 0,
        offres_count: isCommercants ? 0 : count,
      });
      // Toujours réécrire l’autre onglet : no-op si l’API respecte `scope`,
      // sinon restaure la sélection après une proposition globale (API distante).
      const data = isCommercants
        ? await klubMagazineService.replaceOffres(month, keepOther)
        : await klubMagazineService.replaceCommercants(month, keepOther);
      setParution(data);
      notify.success(`Proposition ${label} générée`);
      loadList();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setBusy(false);
    }
  }, [
    ask,
    tab,
    targetCommercants,
    targetOffres,
    targetDates,
    targetAgendaDates,
    isDatesTab,
    datesService,
    commercants,
    offres,
    dates,
    month,
    toOffrePayload,
    toCommercantPayload,
    loadList,
  ]);

  const handleExport = useCallback(async () => {
    setBusy(true);
    try {
      if (tab === 'commercants') {
        await klubMagazineService.exportCommercants(month);
      } else if (tab === 'offres') {
        await klubMagazineService.exportOffres(month);
      } else {
        await datesApiForTab(tab).exportXml(month);
      }
      notify.success('Export téléchargé');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setBusy(false);
    }
  }, [tab, month]);

  const handleClearSelection = useCallback(async () => {
    const isSetLike = tab === 'set' || tab === 'agenda';
    const isCommercants = tab === 'commercants';
    const count = isSetLike ? dates.length : (isCommercants ? commercants.length : offres.length);
    if (!count) return;
    const label = isSetLike ? 'dates' : (isCommercants ? 'commerçants' : 'offres');
    const ok = await ask({
      title: 'Vider la sélection ?',
      message: isSetLike
        ? `Supprimer les ${count} dates de ${formatMonthLabel(month)} ?`
        : `Supprimer les ${count} ${label} de ${formatMonthLabel(month)} ? Les statistiques d’historique seront mises à jour.`,
      confirmLabel: 'Tout supprimer',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    const cleared = isSetLike
      ? await persistDates([])
      : (isCommercants ? await persistCommercants([]) : await persistOffres([]));
    if (cleared) {
      notify.success(`Sélection ${label} vidée`);
      if (!isSetLike) loadList();
    }
  }, [ask, tab, commercants, offres, dates, month, persistCommercants, persistOffres, persistDates, loadList]);

  const onDragEndCommercants = useCallback(async (event) => {
    const { active, over } = event;
    if (!editable || !over || active.id === over.id) return;
    const oldIndex = commercants.findIndex((c) => String(c.id) === String(active.id));
    const newIndex = commercants.findIndex((c) => String(c.id) === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(commercants, oldIndex, newIndex);
    if (!isNouveauBeforeAncien(next)) {
      notify.warning('Impossible de placer un commerçant ancien avant un nouveau.');
      return;
    }
    await persistCommercants(next);
  }, [editable, commercants, persistCommercants]);

  const onDragEndOffres = useCallback(async (event) => {
    const { active, over } = event;
    if (!editable || !over || active.id === over.id) return;
    const oldIndex = offres.findIndex((o) => String(o.id) === String(active.id));
    const newIndex = offres.findIndex((o) => String(o.id) === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(offres, oldIndex, newIndex);
    if (!isNouveauBeforeAncien(next)) {
      notify.warning('Impossible de placer une offre ancienne avant une nouvelle.');
      return;
    }
    await persistOffres(next);
  }, [editable, offres, persistOffres]);

  const onDragEndDates = useCallback(async (event) => {
    const { active, over } = event;
    if (!editable || !over || active.id === over.id) return;
    const oldIndex = dates.findIndex((d) => String(d.id) === String(active.id));
    const newIndex = dates.findIndex((d) => String(d.id) === String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    await persistDates(arrayMove(dates, oldIndex, newIndex));
  }, [editable, dates, persistDates]);

  const openEditCommercant = useCallback((row) => {
    setEditCommercant(row);
    setCommercantForm({
      nom: row.nom ?? '',
      activite: row.activite ?? '',
      complement: row.complement ?? '',
      copyright: row.copyright ?? '',
      photo_path: row.photo_path ?? '',
      is_nouveau: Boolean(row.is_nouveau),
    });
  }, []);

  const openEditOffre = useCallback((row) => {
    setEditOffre(row);
    setOffreForm({
      commercant_label: row.commercant_label ?? '',
      description: row.description ?? '',
      remise: row.remise ?? '',
      prix: row.prix ?? '',
      prix_barre: row.prix_barre ?? '',
      apartirde: row.apartirde ?? '',
      copyright: row.copyright ?? '',
      photo_path: row.photo_path ?? '',
      is_nouveau: Boolean(row.is_nouveau),
    });
  }, []);

  const closeEditCommercant = useCallback(() => setEditCommercant(null), []);
  const closeEditOffre = useCallback(() => setEditOffre(null), []);
  const closeEditDate = useCallback(() => setEditDate(null), []);

  const openEditDate = useCallback((row) => {
    setEditDate(row);
    setDateForm({
      thematique: row.thematique ?? '',
      titre: row.titre ?? '',
      date_label: row.date_label ?? '',
      resume: row.resume ?? '',
      prix: row.prix ?? '',
      infos: row.infos ?? '',
      lieu: row.lieu ?? '',
    });
  }, []);

  const saveCommercant = useCallback(async () => {
    if (!editCommercant) return;
    const nextFlag = Boolean(commercantForm.is_nouveau);
    if (nextFlag !== Boolean(editCommercant.is_nouveau)) {
      const nextRows = commercants.map((c) => (
        c.id === editCommercant.id ? { ...c, is_nouveau: nextFlag } : c
      ));
      const sorted = [
        ...nextRows.filter((c) => c.is_nouveau),
        ...nextRows.filter((c) => !c.is_nouveau),
      ];
      if (!isNouveauBeforeAncien(sorted)) {
        notify.warning('Impossible de placer un commerçant ancien avant un nouveau.');
        return;
      }
    }

    setBusy(true);
    try {
      const updated = await klubMagazineService.patchCommercant(month, editCommercant.id, {
        ...commercantForm,
        is_nouveau: nextFlag,
      });
      let rows = commercants.map((c) => (c.id === updated.id ? updated : c));
      rows = [
        ...rows.filter((c) => c.is_nouveau),
        ...rows.filter((c) => !c.is_nouveau),
      ];
      const data = await klubMagazineService.replaceCommercants(month, toCommercantPayload(rows));
      setParution(data);
      setEditCommercant(null);
      notify.success('Commerçant mis à jour');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setBusy(false);
    }
  }, [editCommercant, commercantForm, commercants, month, toCommercantPayload]);

  const saveOffre = useCallback(async () => {
    if (!editOffre) return;
    setBusy(true);
    try {
      const payload = {
        ...offreForm,
        is_nouveau: Boolean(offreForm.is_nouveau),
        remise: offreForm.remise === '' ? null : Number(offreForm.remise),
        prix: offreForm.prix === '' ? null : Number(offreForm.prix),
        prix_barre: offreForm.prix_barre === '' ? null : Number(offreForm.prix_barre),
      };
      const updated = await klubMagazineService.patchOffre(month, editOffre.id, payload);
      let rows = offres.map((o) => (o.id === updated.id ? updated : o));
      rows = [
        ...rows.filter((o) => o.is_nouveau),
        ...rows.filter((o) => !o.is_nouveau),
      ];
      if (!isNouveauBeforeAncien(rows)) {
        notify.warning('Impossible de placer une offre ancienne avant une nouvelle.');
        return;
      }
      const data = await klubMagazineService.replaceOffres(month, toOffrePayload(rows));
      setParution(data);
      setEditOffre(null);
      notify.success('Offre mise à jour');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setBusy(false);
    }
  }, [editOffre, offreForm, offres, month, toOffrePayload]);

  const saveInlineField = useCallback(async (kind, row, field, value) => {
    let nextValue = value;
    if (kind === 'offre' && ['remise', 'prix', 'prix_barre'].includes(field)) {
      const raw = String(value ?? '').trim().replace(',', '.');
      if (raw === '') {
        nextValue = null;
      } else {
        const number = Number(raw);
        if (Number.isNaN(number)) {
          notify.warning('Nombre invalide');
          return;
        }
        nextValue = number;
      }
    }

    if (kind === 'date') {
      const rows = dates.map((item) => (item.id === row.id ? { ...item, [field]: nextValue } : item));
      await persistDates(rows);
      return;
    }
    if (kind === 'offre') {
      const rows = offres.map((item) => (item.id === row.id ? { ...item, [field]: nextValue } : item));
      await persistOffres(rows);
      return;
    }
    const rows = commercants.map((item) => (item.id === row.id ? { ...item, [field]: nextValue } : item));
    await persistCommercants(rows);
  }, [dates, offres, commercants, persistDates, persistOffres, persistCommercants]);

  const toggleNouveau = useCallback(async (kind, row) => {
    const source = kind === 'offre' ? offres : commercants;
    const nextRows = source.map((item) => (
      item.id === row.id ? { ...item, is_nouveau: !item.is_nouveau } : item
    ));
    const sorted = [
      ...nextRows.filter((item) => item.is_nouveau),
      ...nextRows.filter((item) => !item.is_nouveau),
    ];
    if (kind === 'offre') {
      await persistOffres(sorted);
      return;
    }
    await persistCommercants(sorted);
  }, [offres, commercants, persistOffres, persistCommercants]);

  const saveDate = useCallback(async () => {
    if (!editDate) return;
    const datesTab = tab === 'agenda' ? 'agenda' : 'set';
    const api = datesApiForTab(datesTab);
    setBusy(true);
    try {
      const updated = await api.patchDate(month, editDate.id, dateForm);
      const rows = dates.map((d) => (d.id === updated.id ? { ...d, ...updated } : d));
      const data = await api.replaceDates(month, toDatePayload(rows));
      if (datesTab === 'agenda') {
        setAgendaExportParution(data);
      } else {
        setAgendaParution(data);
      }
      setEditDate(null);
      notify.success('Date mise à jour');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setBusy(false);
    }
  }, [editDate, dateForm, dates, month, toDatePayload, tab]);

  const handleRewrite = useCallback(async (ids = null) => {
    const count = ids?.length || dates.length;
    if (!count) return;
    const datesTab = tab === 'agenda' ? 'agenda' : 'set';
    const api = datesApiForTab(datesTab);
    const ok = await ask({
      title: ids?.length === 1 ? 'Réécrire cette date ?' : 'Réécrire avec l’IA ?',
      message: ids?.length === 1
        ? 'Le titre et le résumé de cette date seront retravaillés. Vous pourrez encore les éditer ensuite.'
        : `L’IA va retravailler le titre et le résumé des ${count} date(s). L’export XML utilisera ensuite ces textes tels quels.`,
      confirmLabel: 'Réécrire',
      confirmVariant: 'primary',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const data = await api.rewrite(month, ids);
      if (datesTab === 'agenda') {
        setAgendaExportParution(data);
      } else {
        setAgendaParution(data);
      }
      if (editDate && ids?.length === 1) {
        const updated = (data.dates ?? []).find((d) => d.id === editDate.id);
        if (updated) {
          setEditDate(updated);
          setDateForm({
            thematique: updated.thematique ?? '',
            titre: updated.titre ?? '',
            date_label: updated.date_label ?? '',
            resume: updated.resume ?? '',
            prix: updated.prix ?? '',
            infos: updated.infos ?? '',
            lieu: updated.lieu ?? '',
          });
        }
      }
      notify.success('Textes réécrits');
    } catch (err) {
      notify.error(err.message);
    } finally {
      setBusy(false);
    }
  }, [ask, dates.length, month, editDate, tab]);

  const removeCommercant = useCallback(async (row) => {
    if (!editable) return;
    const ok = await ask({
      title: 'Retirer ce commerçant ?',
      message: row.nom,
      confirmLabel: 'Retirer',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    await persistCommercants(commercants.filter((c) => c.id !== row.id));
    notify.success('Commerçant retiré');
  }, [editable, ask, persistCommercants, commercants]);

  const removeOffre = useCallback(async (row) => {
    if (!editable) return;
    const ok = await ask({
      title: 'Retirer cette offre ?',
      message: row.commercant_label || `Offre #${row.offre_id}`,
      confirmLabel: 'Retirer',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    await persistOffres(offres.filter((o) => o.id !== row.id));
    notify.success('Offre retirée');
  }, [editable, ask, persistOffres, offres]);

  const removeDate = useCallback(async (row) => {
    if (!editable) return;
    const ok = await ask({
      title: 'Retirer cette date ?',
      message: row.titre || `Date #${row.agenda_id}`,
      confirmLabel: 'Retirer',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    await persistDates(dates.filter((d) => d.id !== row.id));
    notify.success('Date retirée');
  }, [editable, ask, persistDates, dates]);

  const openPicker = useCallback(async () => {
    const kind = tab === 'offres'
      ? 'offres'
      : (tab === 'set' || tab === 'agenda' ? tab : 'commercants');
    const ym = canonicalMonth(month);
    const api = datesApiForTab(kind);
    setBusy(true);
    try {
      if (kind === 'commercants') {
        const data = await klubMagazineService.candidates(ym);
        setCandidates(data);
      } else if (kind === 'offres') {
        const data = await klubMagazineService.offreCandidates(ym);
        setOffreCandidates(data);
      } else {
        const [cData, tData] = await Promise.all([
          api.candidates({ month: ym }),
          api.thematiques().catch(() => ({ thematiques: [] })),
        ]);
        setDateCandidates(cData.candidates ?? cData ?? []);
        setThematiques(tData.thematiques ?? []);
        setPickerThematiqueId('');
        setPickerDateFrom(`${ym}-01`);
        setPickerDateTo('');
      }
      setPickerKind(kind);
      setSelectedCandidateIds([]);
      setCandidateSearch('');
      setCandidateBucket('all');
      setPickerOpen(true);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setBusy(false);
    }
  }, [tab, month]);

  const closePicker = useCallback(() => setPickerOpen(false), []);

  const activeCandidateSource = pickerKind === 'offres' ? offreCandidates : candidates;

  const flatCandidates = useMemo(() => {
    if (!activeCandidateSource) return [];
    const map = new Map();
    ['nouveaux', 'non_parue', 'parue', 'a_renouveller'].forEach((bucket) => {
      (activeCandidateSource[bucket] ?? []).forEach((c) => {
        if (!map.has(c.id)) {
          map.set(c.id, { ...c, buckets: [bucket] });
        } else {
          map.get(c.id).buckets.push(bucket);
        }
      });
    });
    return Array.from(map.values()).sort(compareProposeOrder);
  }, [activeCandidateSource]);

  const filteredCandidates = useMemo(() => {
    const q = candidateSearch.trim().toLowerCase();
    const already = pickerKind === 'offres'
      ? new Set(offres.map((o) => o.offre_id))
      : new Set(commercants.map((c) => c.commercant_id));
    return flatCandidates
      .filter((c) => !already.has(c.id))
      .filter((c) => {
        if (candidateBucket === 'all') return true;
        return (c.buckets ?? []).includes(candidateBucket);
      })
      .filter((c) => {
        if (!q) return true;
        if (pickerKind === 'offres') {
          return `${c.commercant_label} ${c.description} ${c.slug}`.toLowerCase().includes(q);
        }
        return `${c.nom} ${c.dirigeant} ${c.slug}`.toLowerCase().includes(q);
      });
  }, [flatCandidates, candidateSearch, candidateBucket, commercants, offres, pickerKind]);

  const filteredSetCandidates = useMemo(() => {
    const already = new Set(dates.map((d) => d.agenda_id));
    const q = candidateSearch.trim().toLowerCase();
    return (dateCandidates ?? []).filter((c) => {
      if (already.has(c.id)) return false;
      if (pickerThematiqueId && String(c.thematique_id) !== String(pickerThematiqueId)
        && !(c.thematiques ?? []).some((t) => String(t.id) === String(pickerThematiqueId))) {
        return false;
      }
      if (pickerDateFrom && c.date_de_debut && c.date_de_debut < pickerDateFrom) return false;
      if (pickerDateTo && c.date_de_debut && c.date_de_debut > pickerDateTo) return false;
      if (!q) return true;
      return `${c.titre} ${c.lieu} ${c.commune} ${c.thematique}`.toLowerCase().includes(q);
    });
  }, [dateCandidates, dates, candidateSearch, pickerThematiqueId, pickerDateFrom, pickerDateTo]);

  const addSelectedCandidates = useCallback(async () => {
    if (!selectedCandidateIds.length) return;

    if (pickerKind === 'set' || pickerKind === 'agenda') {
      const byId = new Map((dateCandidates ?? []).map((c) => [c.id, c]));
      const added = selectedCandidateIds.map((id) => {
        const src = byId.get(id) || {};
        return {
          agenda_id: id,
          thematique: src.thematique,
          titre: src.titre,
          date_label: src.date_label,
          resume: src.resume,
          prix: src.prix,
          infos: src.infos,
          lieu: src.lieu,
          image_path: src.image_path,
        };
      });
      const current = pickerKind === 'agenda' ? agendaDates : setDates;
      const api = datesApiForTab(pickerKind);
      setBusy(true);
      try {
        const saved = await api.replaceDates(month, toDatePayload([...current, ...added]));
        const addedAgendaIds = new Set(added.map((date) => Number(date.agenda_id)));
        const snapshotIds = (saved.dates ?? [])
          .filter((date) => addedAgendaIds.has(Number(date.agenda_id)))
          .map((date) => date.id)
          .filter((id) => id != null);
        let data = saved;
        let rewritten = false;
        if (snapshotIds.length) {
          try {
            data = await api.rewrite(month, snapshotIds);
            rewritten = true;
          } catch (err) {
            notify.error(`Dates ajoutées, mais la réécriture a échoué. ${err.message || ''}`.trim());
          }
        }
        if (pickerKind === 'agenda') {
          setAgendaExportParution(data);
        } else {
          setAgendaParution(data);
        }
        setPickerOpen(false);
        if (rewritten || snapshotIds.length === 0) {
          notify.success(
            rewritten
              ? `${added.length} date(s) ajoutée(s), titre et résumé retravaillés`
              : `${added.length} date(s) ajoutée(s)`,
          );
        }
      } catch (err) {
        notify.error(err.message);
      } finally {
        setBusy(false);
      }
      return;
    }

    const byId = new Map(flatCandidates.map((c) => [c.id, c]));
    const toAdd = [...selectedCandidateIds]
      .sort((a, b) => compareProposeOrder(byId.get(a) || {}, byId.get(b) || {}));

    if (pickerKind === 'offres') {
      const added = toAdd.map((id) => ({
        offre_id: id,
        is_nouveau: Boolean(byId.get(id)?.is_nouveau),
        statut: byId.get(id)?.statut || primaryStatut(byId.get(id)),
        commercant_label: byId.get(id)?.commercant_label,
        description: byId.get(id)?.description,
        photo_path: byId.get(id)?.photo_path,
        remise: byId.get(id)?.remise,
      }));
      const next = [
        ...offres.filter((o) => o.is_nouveau),
        ...added.filter((o) => o.is_nouveau),
        ...offres.filter((o) => !o.is_nouveau),
        ...added.filter((o) => !o.is_nouveau),
      ];
      const ok = await persistOffres(next);
      if (ok) {
        setPickerOpen(false);
        notify.success(`${toAdd.length} offre(s) ajoutée(s)`);
      }
      return;
    }

    const added = toAdd.map((id) => ({
      commercant_id: id,
      is_nouveau: Boolean(byId.get(id)?.is_nouveau),
      statut: byId.get(id)?.statut || primaryStatut(byId.get(id)),
      nom: byId.get(id)?.nom,
    }));
    const next = [
      ...commercants.filter((c) => c.is_nouveau),
      ...added.filter((c) => c.is_nouveau),
      ...commercants.filter((c) => !c.is_nouveau),
      ...added.filter((c) => !c.is_nouveau),
    ];
    const ok = await persistCommercants(next);
    if (ok) {
      setPickerOpen(false);
      notify.success(`${toAdd.length} commerçant(s) ajouté(s)`);
    }
  }, [
    selectedCandidateIds,
    flatCandidates,
    pickerKind,
    offres,
    commercants,
    persistOffres,
    persistCommercants,
    dateCandidates,
    setDates,
    agendaDates,
    month,
    toDatePayload,
  ]);

  const toggleCandidate = useCallback((id) => {
    setSelectedCandidateIds((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ));
  }, []);

  const exportDisabled = busy || (
    tab === 'commercants'
      ? !commercants.length
      : (tab === 'offres' ? !offres.length : !dates.length)
  );
  const targetCount = tab === 'commercants'
    ? targetCommercants
    : (tab === 'offres'
      ? targetOffres
      : (tab === 'agenda' ? targetAgendaDates : targetDates));
  const setTargetCount = tab === 'commercants'
    ? setTargetCommercants
    : (tab === 'offres'
      ? setTargetOffres
      : (tab === 'agenda' ? setTargetAgendaDates : setTargetDates));
  const targetLabel = tab === 'commercants' ? 'Pros' : (tab === 'offres' ? 'Offres' : 'Dates');
  const targetInputId = tab === 'commercants'
    ? 'mag-target-commercants'
    : (tab === 'offres'
      ? 'mag-target-offres'
      : (tab === 'agenda' ? 'mag-target-agenda-dates' : 'mag-target-dates'));
  const targetMax = tab === 'agenda' ? 200 : 100;

  return {
    modal,
    error,
    tab,
    setTab,
    month,
    setMonth,
    monthOptions,
    busy,
    loading,
    editable,
    sensors,
    commercants,
    offres,
    dates,
    setDates,
    agendaDates,
    isDatesTab,
    targetCount,
    setTargetCount,
    targetLabel,
    targetInputId,
    targetMax,
    exportDisabled,
    resolveCommercantStatut,
    resolveOffreStatut,
    handlePropose,
    openPicker,
    handleExport,
    handleRewrite,
    handleClearSelection,
    onDragEndCommercants,
    onDragEndOffres,
    onDragEndDates,
    openEditCommercant,
    openEditOffre,
    openEditDate,
    removeCommercant,
    removeOffre,
    removeDate,
    editCommercant,
    editOffre,
    editDate,
    commercantForm,
    setCommercantForm,
    offreForm,
    setOffreForm,
    dateForm,
    setDateForm,
    closeEditCommercant,
    closeEditOffre,
    closeEditDate,
    saveCommercant,
    saveOffre,
    saveDate,
    saveInlineField,
    toggleNouveau,
    pickerOpen,
    pickerKind,
    candidateSearch,
    setCandidateSearch,
    candidateBucket,
    setCandidateBucket,
    selectedCandidateIds,
    activeCandidateSource,
    flatCandidates,
    filteredCandidates,
    filteredSetCandidates,
    thematiques,
    pickerThematiqueId,
    setPickerThematiqueId,
    pickerDateFrom,
    setPickerDateFrom,
    pickerDateTo,
    setPickerDateTo,
    closePicker,
    toggleCandidate,
    addSelectedCandidates,
  };
}

export default useKlubMagazine;
